/**
 * The two minutes after "Build the draft", shown honestly.
 *
 * Every line on this screen comes off the row the workflow is writing as it goes
 * (`draft_stage`, `draft_pages_total`) and the `pages` table filling one row per fetch -
 * nothing here is on a timer pretending to be progress. That matters because the two
 * failure modes look identical from outside: a big site that takes three minutes and a run
 * that died. A panel that counts real pages tells them apart; a spinner cannot.
 *
 * It is also DURABLE, which the old inline `for(;;)` poller in NewDemoPage was not: the
 * polling lives in an effect keyed on the demo, so a reload, a second tab or a walk back in
 * from the demos list picks the same run up where it is.
 */

import { useEffect, useMemo, useState } from "react";
import { api, post } from "./api";
import type { DraftRun, DraftStage } from "./types";
import { Button, Card, CardHead, Note } from "./ui";

const POLL_MS = 1500;
/** After this long with the row still on `drafting`, ask the workflow itself (one RPC). */
const STALL_MS = 4 * 60_000;
/** After this long, say so. A big site really can take this, and silence reads as broken. */
const SLOW_MS = 3 * 60_000;

const STAGES: { key: DraftStage; label: string; done: string }[] = [
  { key: "discovering", label: "Finding their pages", done: "Found their pages" },
  { key: "reading", label: "Reading the site", done: "Read the site" },
  { key: "offsite", label: "Looking them up elsewhere", done: "Looked them up elsewhere" },
  { key: "extracting", label: "Pulling out the facts", done: "Pulled out the facts" },
  { key: "verifying", label: "Checking every quote against the page", done: "Checked every quote" },
  { key: "writing", label: "Writing the assistant", done: "Wrote the assistant" },
  { key: "saving", label: "Putting the draft together", done: "Put the draft together" },
];

export type DraftPhase = "running" | "ready" | "failed";

/**
 * Polls `GET /v1/demos/:id/draft` while the run is going and stops the moment it is not.
 *
 * A `setTimeout` chain rather than an interval: a slow answer must not queue a second
 * request behind it. Failed polls are counted, not swallowed - three in a row says so on
 * screen, because "the draft is still going" and "your laptop lost the wifi" are different
 * news. `attempt` restarts the whole thing after a retry.
 */
export function useDraftProgress(demoId: string, attempt = 0) {
  const [run, setRun] = useState<DraftRun | null>(null);
  const [offline, setOffline] = useState(false);
  const [dead, setDead] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let misses = 0;
    let asked = false;
    const from = Date.now();

    async function tick(): Promise<void> {
      let stalled = false;
      try {
        stalled = !asked && Date.now() - from > STALL_MS;
        const body = await api<DraftRun>(`/v1/demos/${demoId}/draft${stalled ? "?run=1" : ""}`);
        if (cancelled) return;
        misses = 0;
        setOffline(false);
        setRun(body);
        if (body.status !== "drafting") return;
        if (stalled) {
          asked = true;
          // The row says drafting but the instance is gone, so nothing will ever write the
          // failure onto it. Say it here instead of spinning until someone gives up.
          const state = body.run?.status;
          if (!body.run || state === "errored" || state === "terminated" || state === "unknown") {
            setDead(true);
            return;
          }
        }
      } catch {
        if (cancelled) return;
        misses += 1;
        if (misses >= 3) setOffline(true);
      }
      if (!cancelled) timer = setTimeout(() => void tick(), POLL_MS);
    }

    setDead(false);
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [demoId, attempt]);

  const phase: DraftPhase = dead || (run && run.status !== "drafting" && run.status !== "review")
    ? "failed"
    : run?.status === "review" ? "ready" : "running";

  return { run, phase, offline, dead };
}

/**
 * The raw message the workflow threw, said to a salesperson.
 *
 * These strings are thrown from `src/research/*` and `src/workflows/draft.ts` and used to
 * reach the screen verbatim - "no page could be read from the website" is true but tells
 * nobody what to do next. The raw text is still shown underneath, because the person
 * reading it may well be the one who can fix it.
 */
export function friendlyDraftError(raw: string | null): string {
  const text = raw ?? "";
  if (/no page could be read/i.test(text)) {
    return "Nothing on that site could be read. Some sites turn readers away - their Google Maps or Facebook page usually works instead.";
  }
  if (/has no website to research/i.test(text)) return "This demo has no website to read. Add one and try again.";
  if (/^Set [A-Z_]+ (and [A-Z_]+ )?on the Worker/i.test(text)) {
    return "The Worker is missing its research keys. That is a setup fix, not a problem with this site.";
  }
  if (/OpenAI \d*4?29|rate limit/i.test(text)) return "The model was busy. Give it a minute and try again.";
  if (/OpenAI 5\d\d/i.test(text)) return "The model had a bad minute. Try again.";
  if (/Keenable \w* ?40[12]/i.test(text)) return "The research service turned us away - its key needs checking.";
  if (/Keenable \w* ?429/i.test(text)) return "The research service is rate limiting us. Try again shortly.";
  if (/the model refused/i.test(text)) return "The model would not answer about this site. Try again, or add a note about what they do.";
  return text || "The draft stopped without saying why.";
}

const mmss = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** A page's own title, else the path it lives at - never a bare origin. */
const pageName = (page: { url: string; title: string }) => {
  if (page.title.trim()) return page.title.trim();
  try {
    const path = new URL(page.url).pathname.replace(/\/$/, "");
    return path || "Home";
  } catch {
    return page.url;
  }
};

function Tick() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * The panel. `onReady` fires once when the row reaches `review`; the review screen reloads
 * the demo and takes the page back off us.
 */
export function DraftProgress({ demoId, onReady }: { demoId: string; onReady: () => void }) {
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const { run, phase, offline, dead } = useDraftProgress(demoId, attempt);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (phase !== "running") return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase === "ready") onReady();
  }, [phase, onReady]);

  const current = useMemo(() => {
    const index = STAGES.findIndex((stage) => stage.key === run?.stage);
    return index === -1 ? 0 : index;
  }, [run?.stage]);

  async function again() {
    setRetrying(true);
    setRetryError(null);
    try {
      await post(`/v1/demos/${demoId}/draft`, {});
      setAttempt((n) => n + 1);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : "Could not start the draft.");
    } finally {
      setRetrying(false);
    }
  }

  if (phase === "failed") {
    const message = dead && !run?.error
      ? "The run stopped before it finished, without saying why."
      : friendlyDraftError(run?.error ?? null);
    return (
      <main className="flex flex-col gap-4 px-4 sm:px-6 md:px-10 py-8 sm:py-10 grow items-start">
        <Card className="w-full max-w-[640px]">
          <CardHead title="The draft did not finish" />
          <div className="flex flex-col gap-4 px-5 py-5">
            <div className="text-[14px] leading-relaxed text-ink">{message}</div>
            {run?.error && (
              <div className="px-3 py-2 bg-paper-soft border border-line-soft rounded-lg font-mono text-[11px] leading-relaxed text-muted break-words">
                {run.error}
              </div>
            )}
            {retryError && <div className="text-[13px] text-warn">{retryError}</div>}
            <div className="flex items-center gap-3 flex-wrap">
              <Button kind="primary" disabled={retrying} onClick={() => void again()}>
                {retrying ? "Starting…" : "Try again"}
              </Button>
              <span className="text-[13px] text-muted">Nothing was published, and nothing is half-written: a new run starts from the site again.</span>
            </div>
          </div>
        </Card>
      </main>
    );
  }

  const started = run?.started_at ?? null;
  const elapsed = started ? now - started : 0;
  const read = run?.pages.length ?? 0;
  const total = run?.pages_total ?? null;
  const recent = (run?.pages ?? []).slice(-4);

  return (
    <main className="flex gap-8 px-4 sm:px-6 md:px-10 py-8 sm:py-10 grow items-start flex-wrap">
      <Card className="w-full max-w-[640px]">
        <CardHead
          title="Reading their site"
          right={<span className="font-mono text-[13px] text-muted tabular-nums">{started ? mmss(elapsed) : "…"}</span>}
        />
        <div className="flex flex-col gap-2.5 px-5 py-5">
          {STAGES.map((stage, i) => {
            const state = i < current ? "done" : i === current ? "now" : "next";
            const counting = (stage.key === "reading" || stage.key === "offsite") && state === "now";
            return (
              <div key={stage.key} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2.5 text-[14px]">
                  <span className="flex items-center justify-center w-4 shrink-0">
                    {state === "done"
                      ? <span className="text-ok"><Tick /></span>
                      : state === "now"
                        ? <span className="w-2 h-2 rounded-full bg-warn motion-safe:animate-pulse" />
                        : <span className="w-2 h-2 rounded-full border border-faint" />}
                  </span>
                  <span className={state === "next" ? "text-faint" : state === "done" ? "text-muted" : "font-medium text-ink"}>
                    {state === "done" ? stage.done : stage.label}
                  </span>
                  {counting && (
                    <span className="ml-auto font-mono text-[13px] text-muted tabular-nums">
                      {total ? `${Math.min(read, total)} of ${total}` : `${read}`}
                    </span>
                  )}
                  {state === "done" && stage.key === "discovering" && total && (
                    <span className="ml-auto font-mono text-[13px] text-muted tabular-nums">{total} pages</span>
                  )}
                </div>
                {counting && recent.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pl-[26px]">
                    {recent.map((page) => (
                      <span
                        key={page.url}
                        title={page.url}
                        className={`px-2 py-0.5 rounded-full text-[12px] max-w-[220px] truncate ${
                          page.status >= 400 ? "bg-warn-wash text-warn" : "bg-chip text-muted"
                        }`}
                      >
                        {pageName(page)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 px-5 py-3.5 border-t border-line-soft text-[13px] text-muted flex-wrap">
          {offline
            ? <span className="text-warn">Lost the connection to the console - still trying.</span>
            : elapsed > SLOW_MS
              ? <span>Slower than usual. A big site can take a while; the run carries on either way.</span>
              : <span>Nothing goes live from this step.</span>}
        </div>
      </Card>

      <div className="w-full max-w-[360px]">
        <Note title="You can leave this open or come back to it">
          The draft keeps running on the server. Close the tab, and this demo waits on the demos
          list marked "Drafting…" - opening it again brings you straight back here.
        </Note>
      </div>
    </main>
  );
}
